import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import $ from "jquery";
import { BsHouse, BsShopWindow } from "react-icons/bs";
import { PiBed } from "react-icons/pi";
import { useStateContext } from "../../context/ContextProvider";
import { useNavigate } from "react-router-dom";
import Loading from "../Loading/Loading";

const LeadFormRetailCRM = ({
  developer,
  project,
  leadType,
  enquiryType,
  leadSource,
  language,
}) => {
  const { BACKEND_URL, t, userInfo } = useStateContext();

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    leadName: "",
    leadContact: "",
    leadEmail: "",
    country: userInfo?.state + ", " + userInfo?.countryName ?? "",
    developer: developer ?? "",
    project: project ?? "",
    enquiryType: enquiryType ?? "",
    leadType: leadType ?? "Apartment",
    language: language ?? "English",
    leadSource: leadSource ?? "Website",
    filename: window.location.pathname + window.location.search,
    ip: userInfo?.ip ?? "",
    device: userInfo?.userAgent ?? "",
    notes: "",
  });

  const isOutsideUAE = userInfo?.country !== "United Arab Emirates";

  const [isDuplicate, setIsDuplicate] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [countryCode, setCountryCode] = useState("");
  const [localNumber, setLocalNumber] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [time, setTime] = useState("");

  const contactFieldRef = useRef(null);
  const contactErrorRef = useRef(null);
  const submitBtnRef = useRef(null);
  const timeInputRef = useRef(null);

  let clickId = null;

  // CHECK LAST SUBMISSION
  useEffect(() => {
    const checkLastSubmission = () => {
      const lastSubmission = localStorage.getItem("leadFormSubmission");
      if (lastSubmission) {
        const timeElapsed = Date.now() - parseInt(lastSubmission, 10);
        if (timeElapsed < 300000) {
          setShowForm(false);
          setLoading(false);
          return;
        }
      }
      setShowForm(true);
    };
    checkLastSubmission();
    setLoading(false);
  }, []);

  // URL PATH / FILENAME
  useEffect(() => {
    const path = window.location.pathname;
    const query = window.location.search;
    const fullPath = path + query;
    setFormData((prevData) => ({
      ...prevData,
      filename: fullPath,
    }));
  }, []);

  // SOURCE OF LEAD
  useEffect(() => {
    // GET CURRENT URL
    const urlParams = new URLSearchParams(window.location.search.toLowerCase());
    const fullUrl = window.location.href.toLowerCase();
    console.log(urlParams);
    if (urlParams.has("gclid")) {
      leadSource = "GoogleAds";
      clickId = urlParams.get("gclid");
    } else if (urlParams.has("ttclid")) {
      leadSource = "TikTok";
      clickId = urlParams.get("ttclid");
    } else if (urlParams.has("sccid")) {
      leadSource = "Snapchat";
      clickId = urlParams.get("sccid");
    } else if (urlParams.has("fbclid")) {
      leadSource = "Facebook";
      clickId = urlParams.get("fbclid");
    } else if (urlParams.has("twclid")) {
      leadSource = "Twitter";
      clickId = urlParams.get("twclid");
    } else {
      if (fullUrl.includes("tiktok")) {
        leadSource = "TikTok";
      } else if (fullUrl.includes("facebook") || fullUrl.includes("fb")) {
        leadSource = "Facebook";
      } else if (fullUrl.includes("instagram")) {
        leadSource = "Instagram";
      } else if (fullUrl.includes("twitter")) {
        leadSource = "Twitter";
      } else if (fullUrl.includes("linkedin")) {
        leadSource = "LinkedIn";
      } else if (fullUrl.includes("snapchat")) {
        leadSource = "Snapchat";
      } else if (fullUrl.includes("google")) {
        leadSource = "GoogleAds";
      } else {
        leadSource = formData.leadSource;
      }
    }
    setFormData((prevData) => ({
      ...prevData,
      leadSource: leadSource,
      clickId: clickId,
    }));
  }, []);

  // PHONE NUMBER
  useEffect(() => {
    const phoneField = contactFieldRef.current;
    const fetchIPData = async () => {
      try {
        // IP
        const response = await fetch("https://ipinfo.io/json");
        const data = await response.json();
        const userIp = data.ip || "";
        const countryCode = data.country || "ae";
        const state = data.region || "";

        // TEL INPUT
        const intlTelInputInstance = window.intlTelInput(
          contactFieldRef.current,
          {
            separateDialCode: true,
            initialCountry: "auto",
            geoIpLookup: (callback) => {
              callback(countryCode);
            },
            utilsScript:
              "//cdnjs.cloudflare.com/ajax/libs/intl-tel-input/24.3.3/js/utils.js",
          }
        );

        // Manually trigger an input event after initialization
        setTimeout(() => {
          const event = new Event("input", { bubbles: true });
          contactFieldRef.current.dispatchEvent(event);
        }, 200);

        // SET INITIAL FORM DATA
        setFormData((prevData) => ({
          ...prevData,
          country:
            state + ", " + intlTelInputInstance.getSelectedCountryData().name,
          ip: userIp,
        }));

        // ON COUNTRY CHANGE
        phoneField.addEventListener("countrychange", () => {
          const countryData = intlTelInputInstance.getSelectedCountryData();
          setFormData((prevData) => ({
            ...prevData,
            country: state + ", " + countryData.name,
            ip: userIp,
          }));
        });

        // INPUT VALIDATION
        // contactFieldRef.current
        phoneField.addEventListener("input", () => {
          const phoneNumber = intlTelInputInstance.getNumber();
          const countryCode =
            intlTelInputInstance.getSelectedCountryData().dialCode;
          setCountryCode(countryCode);
          if (intlTelInputInstance.isValidNumber()) {
            setIsValid(true);
            contactErrorRef.current.textContent = "";
            submitBtnRef.current.removeAttribute("disabled");
            const localPhoneNumber = phoneNumber.replace(`+${countryCode}`, "");
            setLocalNumber(localPhoneNumber);

            setFormData((prevData) => ({
              ...prevData,
              leadContact: phoneNumber,
            }));
          } else {
            setIsValid(false);
            contactErrorRef.current.textContent = t("validation_phone");
            submitBtnRef.current.setAttribute("disabled", "true");
          }
        });

        // CLEAN UP ON UNMOUNT
        return () => {
          if (intlTelInputInstance) {
            intlTelInputInstance.destroy();
          }
        };
      } catch (error) {
        console.error("Failed to fetch IP and country information", error);
      }
    };
    fetchIPData();
  }, [showForm]);

  // FORMAT TIME TO 12-HOUR WITH AM/PM
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    let hoursNum = parseInt(hours, 10);
    const ampm = hoursNum >= 12 ? "PM" : "AM";
    hoursNum = hoursNum % 12 || 12; // Convert 0 or 12 to 12
    const formattedHours = hoursNum.toString().padStart(2, "0");
    return `${formattedHours}:${minutes} ${ampm}`;
  };

  // HANDLE TIME CHANGE
  const handleTimeChange = (e) => {
    const timeValue = e.target.value;
    setTime(timeValue);
    const formattedTime = formatTimeTo12Hour(timeValue); // e.g., "01:30 PM"
    setFormData((prevData) => ({
      ...prevData,
      notes: formattedTime ? `${formattedTime}` : "",
    }));
  };

  // HANDLE TIME INPUT CLICK
  const handleTimeClick = () => {
    if (timeInputRef.current) {
      timeInputRef.current.showPicker();
    }
  };

  // HANDLE CHANGE
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "leadContact") {
      setFormData((prevData) => ({
        ...prevData,
        leadContact: countryCode + value,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  // VALIDATE FORM DATA
  const validate = () => {
    const newErrors = {};
    if (!formData.leadName) newErrors.leadName = t("validation_name");
    if (!formData.leadContact) newErrors.leadContact = t("validation_phone");
    return newErrors;
  };

  // HANDLE FORM SUBMISSION
  const handleSubmit = async (e) => {
    e.preventDefault();
    submitBtnRef.current.setAttribute("disabled", "true");

    // Validate form
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors(newErrors);
    setLoading(true);

    try {
      // const url = `${BACKEND_URL}backend/controllers/AddLeadCRM.php`;
      const url = `${BACKEND_URL}create-lead`;
      console.log("url,backend url: ", url, BACKEND_URL);

      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const resData = response.data;
      console.log("Response from backend:", resData);

      if (resData.success) {
        console.log("Form submitted successfully.");
        localStorage.setItem("leadFormSubmission", Date.now().toString());
        setShowForm(false);
      } else {
        console.error("Backend returned an error:", resData.status);
      }

      if (resData.thankyou) {
        setLoading(false);
        if (language === "Arabic") {
          window.location.href = "/thankyou/ar?lead-form-submit=true";
        } else {
          window.location.href = "/thankyou?lead-form-submit=true";
        }
      } else {
        if (resData.blocked) {
          navigate("/blocked");
        } else {
          setLoading(false);
          if (language === "Arabic") {
            navigate("/thankyou/ar");
          } else {
            navigate("/thankyou");
          }
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
      if (error.response) {
        console.error("Server response:", error.response.data);
      } else {
        console.error("Error message:", error.message);
      }
      setLoading(false);
    }
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   // console.log("Form submitted: ", formData);
  //   submitBtnRef.current.setAttribute("disabled", "true");

  //   // Validate form
  //   const newErrors = validate();
  //   if (Object.keys(newErrors).length > 0) {
  //     setErrors(newErrors);
  //     return;
  //   }
  //   setErrors(newErrors);
  //   setLoading(true);

  //   try {
  //     // BACKEND CALL
  //     const url = BACKEND_URL;
  //     // const url = "http://127.0.0.1:8000/";

  //     // AJAX CALL
  //     $.ajax({
  //       url: `${url}backend/controllers/AddLeadCRM.php`,
  //       method: "POST",
  //       contentType: "application/json",
  //       data: JSON.stringify(formData),
  //       dataType: "json",
  //       success: function (response) {
  //         console.log("Response from backend:", response);
  //         if (response.success) {
  //           console.log("Form submitted successfully.");
  //           localStorage.setItem("leadFormSubmission", Date.now().toString());
  //           setShowForm(false);
  //         } else {
  //           console.error("Backend returned an error:", response.status);
  //         }

  //         if (response.thankyou) {
  //           if (language === "Arabic") {
  //             setLoading(false);
  //             // navigate("/thankyou/ar?lead-form-submit=true");
  //             window.location.href = "/thankyou/ar?lead-form-submit=true";
  //           } else {
  //             setLoading(false);
  //             // navigate("/thankyou?lead-form-submit=true");
  //             window.location.href = "/thankyou?lead-form-submit=true";
  //           }
  //         } else {
  //           if (response.blocked) {
  //             navigate("/blocked");
  //           } else {
  //             if (language === "Arabic") {
  //               setLoading(false);
  //               navigate("/thankyou/ar");
  //             } else {
  //               setLoading(false);
  //               navigate("/thankyou");
  //             }
  //           }
  //         }
  //       },
  //       error: function (jqXHR, textStatus, errorThrown) {
  //         console.error("AJAX Error:", textStatus, errorThrown);
  //         console.log("Response Text:", jqXHR.responseText);
  //       },
  //     });
  //   } catch (error) {
  //     console.error(error);
  //     setLoading(false);
  //   }
  // };

  return (
    <>
      {loading && <Loading />}
      <div className="mb-5 flex justify-center items-center">
        <div className="form-container" id="form-container">
          <div className="form-blur"></div>
          {showForm ? (
            <form
              id="lead-form"
              name="lead-form"
              // dir="ltr"
              onSubmit={handleSubmit}
            >
              {/* NAME */}
              <input
                type="text"
                name="leadName"
                id="leadName"
                placeholder={t("name")}
                value={formData.leadName}
                onChange={handleChange}
                required
                autoComplete="true"
              />
              {errors.leadName && (
                <small className="bg-red-600 text-white">
                  {errors.leadName}
                </small>
              )}

              {/* CONTACT NUMBER */}
              <div dir="ltr">
                <input
                  type="tel"
                  ref={contactFieldRef}
                  name="leadContact"
                  id="leadContact"
                  placeholder={t("contact")}
                  value={localNumber}
                  onChange={handleChange}
                  required
                  autoComplete="true"
                />
              </div>
              <small
                id="contactError"
                ref={contactErrorRef}
                className="bg-red-600 text-white"
              ></small>

              {/* EMAIL */}
              {isOutsideUAE && (
                <>
                  <label htmlFor="email">{t("email")}</label>
                  <input
                    type="email"
                    name="leadEmail"
                    id="leadEmail"
                    value={formData.leadEmail}
                    onChange={handleChange}
                    required
                    autoComplete="true"
                  />
                </>
              )}

              {/* PREFERRED CONTACT TIME */}
              <label htmlFor="time">{t("preferred_time")}</label>
              <input
                type="time"
                name="time"
                id="time"
                ref={timeInputRef}
                value={time}
                onChange={handleTimeChange}
                onClick={handleTimeClick}
                placeholder="01:30 PM"
                className="w-full"
                required
              />

              <div className="mt-5">
                <button
                  type="submit"
                  className="submit-btn"
                  id="submit-btn"
                  ref={submitBtnRef}
                  disabled={false}
                >
                  {t("submit")}
                </button>
              </div>
            </form>
          ) : (
            <div className="p-6 text-center my-5">
              {/* {t("thankyou")} */}
              <span className="text-white">
                {language === "Arabic"
                  ? "شكرا لتواصلك معنا."
                  : "Thank you for reaching out to us."}
                &nbsp;
              </span>
              <br />
              {language === "Arabic"
                ? "سيتواصل معك مستشارنا المخصص قريبًا لتلبية احتياجاتك."
                : "Our dedicated consultant will be in contact with you shortly to address your needs."}
              &nbsp;
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default LeadFormRetailCRM;
